/**
 * Secure storage utility
 *
 * Tokens are stored in HttpOnly cookies (set by backend).
 * This module only handles user profile data in sessionStorage.
 */

const USER_KEY = 'user_data'

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
}

/**
 * Encode data to make casual inspection harder (not encryption, just obfuscation)
 */
const encode = (data: string): string => {
  try {
    return btoa(encodeURIComponent(data))
  } catch {
    return data
  }
}

const decode = (data: string): string => {
  try {
    return decodeURIComponent(atob(data))
  } catch {
    return data
  }
}

export const secureStorage = {
  /**
   * Store user data in sessionStorage
   */
  setUser(user: User): void {
    try {
      sessionStorage.setItem(USER_KEY, encode(JSON.stringify(user)))
    } catch {
      // Storage failure - user data will not persist
    }
  },

  /**
   * Get user data
   */
  getUser(): User | null {
    try {
      const encoded = sessionStorage.getItem(USER_KEY)
      if (!encoded) return null
      return JSON.parse(decode(encoded))
    } catch {
      return null
    }
  },

  /**
   * Clear all stored auth data
   */
  clear(): void {
    try {
      sessionStorage.removeItem(USER_KEY)
    } catch {
      // Storage failure
    }
  },

  /**
   * Check if there's a stored user session
   */
  hasSession(): boolean {
    return !!this.getUser()
  },

  /**
   * Clean up any legacy localStorage tokens
   */
  migrateFromLocalStorage(): void {
    try {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      sessionStorage.removeItem('at_secure')
      sessionStorage.removeItem('rt_secure')
    } catch {
      // Migration failure is non-critical
    }
  },
}

// Run migration on module load
secureStorage.migrateFromLocalStorage()
